import React, {useEffect, useState} from "react";
import {Trans, useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import {Link as RouterLink, useLocation, useNavigate} from "react-router-dom";
import PrivacyShieldIcon from "../../components/icons/PrivacyShieldIcon";
import {useStoreConsent} from "../../redux/flows/storeConsentFlow";
import {RootState} from "../../redux/store";
import routes from "../../routes/routes";
import CustomButton from "../layout/components/CustomButton";
import TenantLogo from "../layout/components/TenantLogo";
import ConsentDialog from "./components/ConsentDialog";
import Header from "../layout/components/Header";
import {styled} from "@mui/material/styles";
import {MenuItem, Select, Typography} from "@mui/material";

function getKeyWithDataProtectionInLowercase(
    obj: Record<string, any>
): string | undefined {
    for (const key in obj) {
        if (key.toLowerCase().includes("dataprotection")) {
            return key;
        }
    }
    return undefined;
}

const SelectStyled = styled(Select)(({theme, value}) => ({
    backgroundColor: theme.palette.background.default,
    "& .MuiSelect-select:focus": {
        width: "100%",
        backgroundColor: theme.palette.background.default,
    },
    "& .MuiSelect-select:not(:focus)": {
        backgroundColor: theme.palette.background.default,
        width: "100%",
    },
    height: "62px",
    "& .MuiInputBase-input": {
        color: theme.palette.primary.main,
    },
    "& .MuiOutlinedInput-notchedOutline": {
        borderColor:
            value !== 0
                ? theme.palette.primary.light
                : theme.palette.primary.contrastText,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.light,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.light,
    },
    "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.contrastText,
    },
}));

const MenuItemStyled = styled(MenuItem)(({ theme }) => ({
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(1), // Ensure consistent padding for MenuItems
}));

const ConsentPage = () => {
    const {t} = useTranslation();
    const [consentDialogOpen, setConsentDialogOpen] = useState<boolean>(false);
    const hintPages = useSelector((state: RootState) => state.hints.pages);
    const storeConsent = useStoreConsent(true);
    const navigate = useNavigate();
    const location = useLocation();

    const queryParams = new URLSearchParams(location.search);
    const redirectURL = queryParams.get("redirect") || "/";

    let dataProtectionPath = "/hints";
    if (hintPages) {
        const path = getKeyWithDataProtectionInLowercase(hintPages);
        if (path) {
            console.log(path);
            dataProtectionPath = routes.makePrivacyGDPRPath(path);
        }
    }

    const onAcceptConsent = () => {
        storeConsent();
        navigate(redirectURL);
    };

    const tenants = [
        "kolula",
        "demo",
        "dhu",
        "vonovia",
        "neuland",
        "seezeit",
        "strandbad-pertisau",
        "badesee-petershagen",
        "solheimsviken"
    ]

    const [tenantSelected, setTenantSelected] = useState<string>("kolula");

    const environment = process.env.REACT_APP_ENV ?? "integration";
    const localStorageTenant = localStorage.getItem("tenant");

    const handleTenantSwitch = (tenant: string) => {
        setTenantSelected(tenant);
        localStorage.setItem("tenant", tenant);
        window.location.href = window.location.origin
    }

    useEffect(() => {
        if (localStorageTenant) {
            setTenantSelected(localStorageTenant);
        }
    }, [localStorageTenant]);

    return (
        <div className="flex flex-col items-center px-5">
            <Header>
                <TenantLogo/>
            </Header>
            {environment === "integration" && <SelectStyled
                labelId="demo-select-large-label"
                id="demo-select-small"
                label={"Tenant"}
                value={tenantSelected}
                onChange={(event) => handleTenantSwitch(event.target.value as string)}
            >
                <MenuItemStyled value={0}>
                    <span>{"Select tenant"}</span>
                </MenuItemStyled>
                {tenants.map((tenant, index) => (
                    <MenuItemStyled key={index} value={tenant}>
                        <Typography variant="body2">
                            {tenant}
                        </Typography>
                    </MenuItemStyled>
                ))}
            </SelectStyled>}
            <h1 className="mt-2 text-2xl font-semibold -tracking-[0.0225rem]">
                {t("consent.title")}
            </h1>
            <div className="rounded-full bg-primary/15 p-4 mt-5 mb-5">
                <PrivacyShieldIcon/>
            </div>
            <p className="text-center text-lg leading-7 mb-12">
                <Trans
                    i18nKey="consent.description"
                    components={{
                        a: (
                            <RouterLink
                                className="text-primary underline underline-offset-2 brightness-75"
                                to={dataProtectionPath}
                            ></RouterLink>
                        ),
                    }}
                />
            </p>

            <CustomButton
                isButtonDark={true}
                fullWidth={true}
                onClick={onAcceptConsent}
                color="secondary"
                variant="outlined"
                isOutlined={true}
                children={t("consent.allow")}
            />
            <CustomButton
                isButtonDark={false}
                fullWidth={true}
                onClick={() => setConsentDialogOpen(true)}
                color={"secondary"}
                isOutlined={true}
                children={t("consent.decline")}
            />
            <ConsentDialog
                open={consentDialogOpen}
                onClose={() => setConsentDialogOpen(false)}
            />
        </div>
    );
};

export default ConsentPage;
